import React, { useState, useEffect } from "react";
import { FallingLines } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { ReactComponent as AddUserIcon } from "images/add.svg";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import SkeletonIcon from "images/profile/Skeleton.png";
import AgencyTeamCards from "./cards";
import CreateNewMemberModal from "./agencyTeamModal/upsertNewMemberModal";
import useCreateNewMember from "./hooks/useCreateNewMember";
import useAgencyTeam from "./hooks/useAgencyTeam";
import Pagination from "components/pagination";
import { ReactComponent as SearchIcon } from "images/profile/search-normal.svg";
import useDebounce from "hooks/useDebounce";

function AgencyTeam({}) {
  const [t] = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const navigate = useNavigate();

  const {
    totalCount,
    users,
    initLoading: listLoading,
    filterQuery,
    setFilterQuery,
    defaultFilterQuery,
    setUsers: updateUsersResult,
    userDetails = {},
    userDomains = [],
    userSubDomains = [],
    loadPage,
  } = useAgencyTeam({});

  const userRates = userDetails?.userRates ?? [];
  const userServices = userDetails?.userServices ?? [];

  const { isOpen, setIsOpen, onSubmit, isLoading } = useCreateNewMember({
    updateUsersResult,
    setFilterQuery,
    defaultFilterQuery,
  });

  const user = useSelector((state) => state.user || {});
  const isAgency = user?.isAgency ?? false;
  // Handle debounced search
  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      let searchQuery = debouncedSearchTerm.trim();
      setFilterQuery((prevQuery) => ({
        ...prevQuery,
        filter: debouncedSearchTerm
          ? {
              op: "or",
              value: [
                {
                  op: "or",
                  value: [
                    {
                      op: "lik",
                      key: "first_name",
                      value: searchQuery,
                    },

                    {
                      op: "lik",
                      key: "last_name",
                      value: searchQuery,
                    },
                    {
                      op: "lik",
                      key: "email",
                      value: searchQuery,
                    },
                  ],
                },
              ],
            }
          : null,
        paginate: {
          ...prevQuery.paginate,
          pageNumber: 1, // Reset to first page when searching
        },
      }));
    }
  }, [debouncedSearchTerm]);

  if (loadPage) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <FallingLines
          color="#023A66"
          width="100%"
          visible={true}
          ariaLabel="falling-lines-loading"
        />
      </div>
    );
  }

  if (!isAgency) {
    navigate("/");
  }

  return (
    <>
      <div className="flex p-[24px] flex-col gap-[20px] self-stretch flex-1relative">
        {isOpen && (
          <CreateNewMemberModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onSubmit={onSubmit}
            isLoading={isLoading}
            title={t("addNewMember")}
            userRates={userRates}
            userServices={userServices}
            userDomains={userDomains}
            userSubDomains={userSubDomains}
          />
        )}

        <div
          className="flex w-full flex-col gap-[20px]"
          id="agencyTeamScrollId"
        >
          <div className="flex  gap-[12px] justify-between items-center">
            <div className="text-[#1D1B22] font-semibold text-[30px]">
              {t("agencyTeam")}
            </div>
            <div
              className="flex justify-center items-center gap-[8px] rounded-[6px] cursor-pointer"
              onClick={() => setIsOpen(true)}
            >
              <AddUserIcon className="cursor-pointer" />
            </div>
          </div>

          <div className="flex flex-wrap gap-[16px] self-stretch">
            <div className="relative  w-[249px] z-50">
              <input
                type="text"
                placeholder="Search for Name, Email..."
                autoCorrect="off"
                autoComplete="off"
                spellCheck="false"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`flex h-[44px] w-full items-center  py-[8px] px-[14px]
                    self-stretch rounded-[6px] border border-[#E4E4E7] bg-[#fff]
                    outline-none text-[rgba(29, 27, 34, 0.40)] font-regular
                     text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px]
                     placeholder:not-italic placeholder:font-normal pr-[40px] `}
              />
              <SearchIcon className="absolute top-[10px] right-[14px]" />
            </div>
          </div>

          {listLoading ? (
            <div className="grid  md-max:grid-cols-1 grid-cols-2 items-center gap-[20px] w-full">
              <img
                src={SkeletonIcon}
                alt="SkeletonIcon"
                className="w-full object-cover"
              />
              <img
                src={SkeletonIcon}
                alt="SkeletonIcon"
                className="w-full object-cover"
              />
            </div>
          ) : (
            <AgencyTeamCards
              userDetails={userDetails}
              userDomains={userDomains}
              userSubDomains={userSubDomains}
              users={users}
              updateUsersResult={updateUsersResult}
              filterQuery={filterQuery}
            />
          )}
          {totalCount > 6 && (
            <Pagination
              currentPage={filterQuery.paginate.pageNumber}
              totalPages={Math.ceil(totalCount / filterQuery.paginate.pageSize)}
              onPageChange={(page) => {
                setFilterQuery((prevState) => {
                  return {
                    ...prevState,
                    paginate: {
                      pageNumber: page,
                      pageSize: prevState.paginate.pageSize,
                    },
                  };
                });
                document
                  .getElementById("agencyTeamScrollId")
                  ?.scrollIntoView?.({
                    behavior: "smooth",
                    block: "start",
                  });
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default AgencyTeam;
